/* eslint-disable react/button-has-type */
/* eslint-disable react/no-multi-comp */
/* eslint-disable react/no-array-index-key */
import React from 'react';
import {
  generateComponentDocs,
  generateStoryDocs,
} from '../../utils/documentationUtils';

/**
 * URL Query Params Modal Management Pattern

 * A robust pattern for synchronizing modal state with URL query parameters,
 * enabling deep-linking, browser navigation, and persistent state management.
 */

export default {
  title: 'Patterns/URL Query Params Modal Management',
  parameters: {
    layout: 'padded',
    docs: {
      description: {
        component: `
# URL Query Params Modal Management

A pattern that synchronizes modal state with URL query parameters, enabling deep-linking, 
browser navigation, and consistent state across reloads and shared URLs.

## Benefits

- **🔗 Deep Linking**: Share URLs that open specific modals
- **🧭 Browser Navigation**: Back/forward buttons work with modal state
- **🔄 State Persistence**: Modal state survives page reloads
- **📱 Bookmarkable**: Users can bookmark modal states
- **🔍 SEO Friendly**: Search engines can index modal content

## When to Use

- Modals containing important content that should be shareable
- Multi-step forms or wizards
- Detail views or edit modes
- Any modal state that should persist across navigation
        `,
      },
    },
    ...generateComponentDocs(
      // Problem Statement
      'Synchronizes modal state (open/close, edit/view, etc.) with URL query parameters. This enables deep-linking, browser navigation, and consistent state across reloads and shared URLs.',

      // Implementation Guide
      `## Implementation Steps

### 1. Define Modal Configuration
Configure which modals need URL synchronization and whether they store ID parameters.

### 2. Initialize the Hook
Use the \`useUrlModals\` hook in your component to get modal state management functions.

### 3. Implement Modal Operations
Use the provided \`openModal\` and \`closeModal\` functions to control modal state.

### 4. Connect to Modal Components
Pass the modal state and control functions to your modal components.

## Configuration Options

- **\`isIdParam: true\`**: Modal stores an ID value in the URL parameter
- **\`isIdParam: false\`**: Modal only stores open/closed state

## Best Practices

- Use descriptive modal names that reflect their purpose
- Keep modal parameter names short but meaningful
- Close modals when navigating away from their context
- Handle edge cases like invalid IDs gracefully
      `,
      // Current Usage
      `## Current Implementations

The pattern is actively used across multiple pages and components in the application.`,
      // Related Components
      `## Related Components

Key components and hooks that work together with this pattern.`,
    ),
  },
  tags: ['pattern', 'modals', 'url', 'state-management'],
};

// ----- Dummy Data for Storybook -----
const CODE_EXAMPLES = {
  modalConfig: `const modalConfig = {
  noteModal: { isIdParam: true },
  editMode: { isIdParam: false }
};`,

  hookUsage: 'const { openModal, closeModal, modals } = useUrlModals(modalConfig);',

  modalOperations: `openModal('noteModal', 123);
closeModal('noteModal');`,

  componentIntegration: '<NoteModal isOpen={modals.noteModal.isOpen} onClose={() => closeModal(\'noteModal\')} />',
};

const CURRENT_USAGE = [
  {
    component: 'NotesPage',
    path: '/pagesV2/Notes/NotesPage.js',
    description: 'Uses URL query params to manage note modals (view/edit) and deep-linking for notes.',
  },
  {
    component: 'SiteDetailsPage',
    path: '/componentsV2/Site/SiteDetailsPage/SiteDetailsPage.js',
    description: 'Synchronizes site image preview and note modals with URL query params.',
  },
  {
    component: 'SiteDetailPageTabsContent',
    path: '/componentsV2/Site/SiteDetailsPageTabs/SiteDetailPageTabsContent.js',
    description: 'Manages note view/edit modal state via URL query params within site tabs.',
  },
  {
    component: 'SiteDetailPageTabsWrapper',
    path: '/componentsV2/Site/SiteDetailsPageTabs/SiteDetailPageTabsWrapper.js',
    description: 'Handles add note modal state using URL query params.',
  },
  {
    component: 'SiteDetailPageTabs',
    path: '/componentsV2/Site/SiteDetailsPageTabs/SiteDetailPageTabs.js',
    description: 'Handles note view modal state using URL query params.',
  },
  {
    component: 'DashboardPage',
    path: '/pagesV2/Dashboard/DashboardPage.js',
    description: 'Uses URL query params to control note modal state from dashboard notes.',
  },
  {
    component: 'TagShow',
    path: '/pagesV2/Tags/TagShow.js',
    description: 'Uses URL query params for tag sites modal deep-linking.',
  },
  {
    component: 'ReportsPageWrapper',
    path: '/componentsV2/Exports/ReportsPageWrapper.js',
    description: 'Controls export season modal state via URL query params.',
  },
  {
    component: 'CustomFieldsDisplay',
    path: '/componentsV2/CustomFields/CustomFieldsDisplay.js',
    description: 'Centralizes all modal URL state for custom fields add/edit modals.',
  },
  {
    component: 'CustomFieldsLandingPage',
    path: '/pagesV2/Admin/CustomFields/CustomFieldsLandingPage.js',
    description: 'Handles custom fields modal state via URL query params.',
  },
  {
    component: 'UsersPageWrapper',
    path: '/componentsV2/Users/UsersPageWrapper.js',
    description: 'Manages user add/edit and account details modals with URL query params.',
  },
  {
    component: 'UsersPage',
    path: '/pagesV2/Admin/Users/UsersPage.js',
    description: 'Uses URL query params for user modals (add/edit, details, deactivate).',
  },
  {
    component: 'SeasonsPage',
    path: '/pagesV2/Admin/Seasons/Seasons.js',
    description: 'Handles export and delete season modals via URL query params.',
  },
  {
    component: 'ContactsPage',
    path: '/pagesV2/Contacts/ContactsPage.js',
    description: 'Uses URL query params for add/edit contact modal state.',
  },
  {
    component: 'ContactInfo',
    path: '/componentsV2/Contacts/ContactInfo.js',
    description: 'Synchronizes contact delete modal state with URL query params.',
  },
  {
    component: 'TagsPage',
    path: '/pagesV2/Tags/TagsPage.js',
    description: 'Handles tag modals (add/edit, assign sites) via URL query params.',
  },
  {
    component: 'SiteTagsWrapper',
    path: '/componentsV2/Site/SiteDetailsPage/SiteTagsWrapper.js',
    description: 'Manages tag modal state for site tags using URL query params.',
  },
  {
    component: 'SiteTags',
    path: '/componentsV2/Site/SiteDetailsPage/SiteTags.js',
    description: 'Handles tag delete modal state via URL query params.',
  },
  {
    component: 'RoutePage',
    path: '/pagesV2/Route/RoutePage.js',
    description: 'Uses URL query params for route modals (add/edit stop, details).',
  },
  {
    component: 'RoutesPageWrapper',
    path: '/pagesV2/Routes/RoutesPageWrapper.js',
    description: 'Handles route filter and add route modal state via URL query params.',
  },
  {
    component: 'CustomFieldsAddEditModal',
    path: '/componentsV2/CustomFields/CustomFieldsAddEditModal.js',
    description: 'Receives modal open/close state from URL query params.',
  },
  {
    component: 'MultipleTabsModal',
    path: '/componentsV2/Modals/MultipleTabsModal.js',
    description: 'Supports tabbed modals with URL query param state.',
  },
];

const RELATED_COMPONENTS = [
  {
    name: 'useUrlModals',
    type: 'Hook',
    description: 'Custom hook for managing modal state in sync with URL query params',
  },
  {
    name: 'Modal',
    type: 'Component',
    description: 'Generic modal component that works with useUrlModals',
  },
];

// ----- Main Documentation Story -----
export const Overview = () => (
  <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>

    {/* Quick Start Section */}
    <section style={{ marginBottom: '40px' }}>
      <h2 style={{ borderBottom: '2px solid #e1e5e9', paddingBottom: '8px' }}>
        🚀 Quick Start
      </h2>

      <div style={{
        backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginTop: '16px',
      }}
      >
        <h3>1. Configure Your Modals</h3>
        <pre style={{
          backgroundColor: '#fff', padding: '16px', borderRadius: '4px', overflow: 'auto',
        }}
        >
          <code>{CODE_EXAMPLES.modalConfig}</code>
        </pre>
      </div>

      <div style={{
        backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginTop: '16px',
      }}
      >
        <h3>2. Initialize the Hook</h3>
        <pre style={{
          backgroundColor: '#fff', padding: '16px', borderRadius: '4px', overflow: 'auto',
        }}
        >
          <code>{CODE_EXAMPLES.hookUsage}</code>
        </pre>
      </div>

      <div style={{
        backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginTop: '16px',
      }}
      >
        <h3>3. Control Modal State</h3>
        <pre style={{
          backgroundColor: '#fff', padding: '16px', borderRadius: '4px', overflow: 'auto',
        }}
        >
          <code>{CODE_EXAMPLES.modalOperations}</code>
        </pre>
      </div>

      <div style={{
        backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginTop: '16px',
      }}
      >
        <h3>4. Integrate with Components</h3>
        <pre style={{
          backgroundColor: '#fff', padding: '16px', borderRadius: '4px', overflow: 'auto',
        }}
        >
          <code>{CODE_EXAMPLES.componentIntegration}</code>
        </pre>
      </div>
    </section>

    {/* Current Usage Section */}
    <section style={{ marginBottom: '40px' }}>
      <h2 style={{ borderBottom: '2px solid #e1e5e9', paddingBottom: '8px' }}>
        📍 Current Usage
      </h2>
      <div style={{ display: 'grid', gap: '16px', marginTop: '16px' }}>
        {CURRENT_USAGE.map((usage, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #e1e5e9',
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: '#fdfdfd',
            }}
          >
            <h4 style={{ margin: '0 0 8px 0', color: '#1976d2' }}>{usage.component}</h4>
            <code style={{
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              display: 'block',
              marginBottom: '8px',
            }}
            >
              {usage.path}
            </code>
            <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
              {usage.description}
            </p>
          </div>
        ))}
      </div>
    </section>

    {/* Related Components Section */}
    <section style={{ marginBottom: '40px' }}>
      <h2 style={{ borderBottom: '2px solid #e1e5e9', paddingBottom: '8px' }}>
        🔗 Related Components
      </h2>
      <div style={{ display: 'grid', gap: '16px', marginTop: '16px' }}>
        {RELATED_COMPONENTS.map((component, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #e1e5e9',
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: '#fdfdfd',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px',
            }}
            >
              <h4 style={{ margin: '0', color: '#1976d2' }}>{component.name}</h4>
              <span style={{
                backgroundColor: component.type === 'Hook' ? '#e3f2fd' : '#f3e5f5',
                color: component.type === 'Hook' ? '#1976d2' : '#7b1fa2',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
              >
                {component.type}
              </span>
            </div>
            <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
              {component.description}
            </p>
          </div>
        ))}
      </div>
    </section>

    {/* Tips Section */}
    <section style={{ marginBottom: '40px' }}>
      <h2 style={{ borderBottom: '2px solid #e1e5e9', paddingBottom: '8px' }}>
        💡 Tips & Best Practices
      </h2>
      <div style={{
        backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '8px', padding: '20px', marginTop: '16px',
      }}
      >
        <ul style={{ margin: '0', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}>
            <strong>Descriptive Names:</strong> Use clear, descriptive modal names that reflect their purpose
          </li>
          <li style={{ marginBottom: '8px' }}>
            <strong>URL Cleanup:</strong> Close modals when navigating away from their context
          </li>
          <li style={{ marginBottom: '8px' }}>
            <strong>Error Handling:</strong> Handle edge cases like invalid IDs gracefully
          </li>
          <li style={{ marginBottom: '8px' }}>
            <strong>Performance:</strong> Consider debouncing URL updates for rapidly changing modal state
          </li>
        </ul>
      </div>
    </section>
  </div>
);

Overview.parameters = generateStoryDocs(
  'Complete guide to implementing URL Query Params Modal Management pattern.',
  'Reference this documentation when implementing modals that need to be deep-linkable or persist across navigation.',
);

// ----- Interactive Example Story -----
export const InteractiveExample = () => {
  const [modals, setModals] = React.useState({
    noteModal: { isOpen: false, value: null },
    editMode: { isOpen: false, value: null },
  });

  const openModal = (modalName, value = 'true') => {
    setModals(prev => ({
      ...prev,
      [modalName]: { isOpen: true, value },
    }));
  };

  const closeModal = (modalName) => {
    setModals(prev => ({
      ...prev,
      [modalName]: { isOpen: false, value: null },
    }));
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px' }}>
      <h3>Interactive Demo</h3>
      <p>Try opening and closing modals to see how the state would be managed:</p>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <button
          onClick={() => openModal('noteModal', '123')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Open Note Modal
        </button>
        <button
          onClick={() => closeModal('noteModal')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#d32f2f',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Close Note Modal
        </button>
        <button
          onClick={() => openModal('editMode')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#388e3c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Toggle Edit Mode
        </button>
      </div>

      <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
        <h4>Current Modal State:</h4>
        <pre style={{ margin: '0', fontSize: '14px' }}>
          {JSON.stringify(modals, null, 2)}
        </pre>
      </div>

      <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>
        <p><strong>Note:</strong> In a real implementation, this state would be synchronized with URL query parameters.</p>
      </div>
    </div>
  );
};

InteractiveExample.parameters = generateStoryDocs(
  'Interactive example showing how modal state management works.',
  'Use this demo to understand the pattern behavior before implementing.',
);
